import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import TimeInput from './TimeInput'

import FakeInput from 'components/Common/Form/Input/FakeInput'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Group from 'components/utils/Group'
import { INTL_DMY_CASUAL_SHORT_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import useToggle from 'hooks/useToggle'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import DateRangeDropdown from 'tripPlanner/components/TripModal/Common/DateRangeDropdown'

const OverlappingGroup = styled(Group)`
  * + * {
    margin-inline-start: -1px;
  }
`

const DateInput = styled(FakeInput)`
  &.date-wrap {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    min-width: ${rem(160)};
    height: ${rem(48)};

    &:not(.error):not(.disabled):focus-within {
      z-index: 1;
    }
  }
`
const StyledTimeInput = styled(TimeInput)`
  min-width: ${rem(91)};
`

function DesktopAccommodationDateTimeFields() {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const { watch, setValue, register } = useFormContext()
  const startDate = watch('startDate')
  const endDate = watch('endDate')
  const dateDropdownTrigger = useRef<HTMLDivElement | null>(null)

  const {
    value: dateDropdownOpen,
    toggle: toggleDateDropdown,
    off: closeDateDropdown,
  } = useToggle()

  const onDatesChange = useCallback(
    (dates: { startDate: moment.Moment, endDate?: moment.Moment | null }) => {
      setValue('startDate', dates.startDate.format(ISO_DATE_FORMAT), {
        shouldDirty: true,
        shouldValidate: true,
      })
      setValue('endDate', dates.endDate?.format(ISO_DATE_FORMAT), {
        shouldDirty: true,
        shouldValidate: true,
      })
      closeDateDropdown()
    },
    [closeDateDropdown, setValue],
  )

  const startDateMoment = startDate ?
      moment(startDate, ISO_DATE_FORMAT) :
    undefined
  const endDateMoment = endDate ? moment(endDate, ISO_DATE_FORMAT) : undefined

  const onClickDateInput = useCallback(() => {
    toggleDateDropdown()
  }, [toggleDateDropdown])

  const onClearDates = useCallback(() => {
    setValue('startDate', undefined)
    setValue('endDate', undefined)
    closeDateDropdown()
  }, [closeDateDropdown, setValue])

  const checkInLabel = intl.formatMessage({
    id: 'lRE8lMh6ntF8UYs0GpgG',
    defaultMessage: 'Check-in',
    description: 'Label for check-in date field in desktop accommodation form',
  })

  const checkOutLabel = intl.formatMessage({
    id: 'Jm77m8wB6u7jvzD5Fl_w',
    defaultMessage: 'Check-out',
    description: 'Label for check-out date field in desktop accommodation form',
  })

  const selectDatePlaceholder = intl.formatMessage({
    id: 'JP40h3dtQShiEtxx8pw9',
    defaultMessage: 'Select date',
    description: 'Placeholder text for date picker in desktop accommodation form',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: '-VsPFWhUIa6MSggc7naa',
    defaultMessage: 'Select time',
    description: 'Placeholder text for time picker in desktop accommodation form',
  })

  return (
    <>
      <Group direction="horizontal" gap={16} ref={dateDropdownTrigger}>
        <OverlappingGroup
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="end"
          gap={0}
        >
          <DateInput
            label={checkInLabel}
            value={
              startDate ? dateFormatter(startDate, INTL_DMY_CASUAL_SHORT_FORMAT) : ''
            }
            placeholder={selectDatePlaceholder}
            onClick={onClickDateInput}
            startIcon={<LineCalendarIcon />}
            data-testid="check-in-date"
          />
          <StyledTimeInput
            {...register('startTime')}
            placeholder={selectTimePlaceholder}
            required
            defaultValue="15:00"
            hasIcon={false}
            showClear={false}
            onFocus={closeDateDropdown}
            testIdPrefix="check-in-time"
          />
        </OverlappingGroup>
        <OverlappingGroup
          direction="horizontal"
          horizontalAlign="stretch"
          verticalAlign="end"
          gap={0}
        >
          <DateInput
            label={checkOutLabel}
            value={endDate ? dateFormatter(endDate, INTL_DMY_CASUAL_SHORT_FORMAT) : ''}
            placeholder={selectDatePlaceholder}
            onClick={onClickDateInput}
            startIcon={<LineCalendarIcon />}
            data-testid="check-out-date"
          />
          <StyledTimeInput
            {...register('endTime')}
            placeholder={selectTimePlaceholder}
            required
            defaultValue="10:00"
            hasIcon={false}
            showClear={false}
            onFocus={closeDateDropdown}
            testIdPrefix="check-out-time"
          />
        </OverlappingGroup>
      </Group>
      <DateRangeDropdown
        startDate={startDateMoment}
        endDate={endDateMoment}
        triggerRef={dateDropdownTrigger}
        isOpen={dateDropdownOpen}
        onDatesChange={onDatesChange}
        onClear={onClearDates}
        onClose={closeDateDropdown}
        startLabel={checkInLabel}
        endLabel={checkOutLabel}
        rangeType="nights"
      />
    </>
  )
}

export default DesktopAccommodationDateTimeFields
